import { BookingInitialState } from "stores/booking/types";

// Define an interface for a BookingStore object
interface BookingStore {
  isTravelProtected: boolean;
  totalAmt: number | null;
  discount: number | null;
  achProcessingSavings?: number | null;
}

// Calculate the amount to be paid
export function calculateAmount(bookingStore: Partial<BookingStore>, travelProtectionCost: number, pdpFees: number, rate: number): number {
  const discount = bookingStore?.discount || 0; // Get the discount amount from the booking store object
  const total = bookingStore.totalAmt || 0;
  const travelProtection = bookingStore.isTravelProtected ? travelProtectionCost : 0;
  const achProcessingSavings = bookingStore?.achProcessingSavings || 0;
  const amount = total + pdpFees + travelProtection - discount - achProcessingSavings;
  return amount <= 0 ? 0 : amount * rate; // Return the amount adjusted by the rate
}
interface PaymentModeFeeParams {
  isAffirmPayment?: boolean;
  // Affirm fee only applies on the payment step (PriceDetails guards this with `step === 2`)
  applyAffirmFee?: boolean;
  affirmFeePercentage?: number;
  isKlarnaPayment?: boolean;
  klarnaFeePercentage?: number;
  klarnaFixedFee?: number;
  totalIncludingTaxes?: number;
  totalAmt?: number;
  pdpFees?: number;
}

// Calculate the extra fee charged for deferred payment modes (Affirm / Klarna).
// Returns the raw (pre-currency-rate) fee so callers can add it to their own totals.
// This is the single source of truth shared by PriceDetails and BookingPriceHeader
// so the header total and the price breakdown can't drift apart.
export function calculatePaymentModeFee({
  isAffirmPayment = false,
  applyAffirmFee = false,
  affirmFeePercentage = 0,
  isKlarnaPayment = false,
  klarnaFeePercentage = 0,
  klarnaFixedFee = 0,
  totalIncludingTaxes = 0,
  totalAmt = 0,
  pdpFees = 0
}: PaymentModeFeeParams): number {
  const affirmFee = applyAffirmFee && isAffirmPayment ? ((totalIncludingTaxes || 0) + (pdpFees || 0)) * (affirmFeePercentage || 0) / 100 : 0;
  const klarnaFee = isKlarnaPayment ? ((totalIncludingTaxes || totalAmt) + (pdpFees || 0)) * (klarnaFeePercentage || 0) / 100 + (klarnaFixedFee || 0) : 0;
  return affirmFee + klarnaFee;
}

// Function to calculate half of the total payment
export function calculateOfFirstPayment(rate: number, bookingStore: BookingInitialState, hostFees: number, totalTax: number, serviceFee: number, travelProtectionCost: number, firstPaymentPercentage: number): number {
  const isTravelProtected = bookingStore.isTravelProtected || false;
  const isCancelBookingTravelProtected = bookingStore.isCancelBookingTravelProtected || false;
  const getOriginalServiceFee = bookingStore?.prequote?.originalServiceFee || 0;

  // Calculate the travel protection cost
  const insurance = isTravelProtected && !isCancelBookingTravelProtected ? travelProtectionCost : isCancelBookingTravelProtected ? travelProtectionCost : 0;
  const discount = bookingStore.discount || 0;
  // Calculate the original rent with the given rate
  const originalRent = bookingStore.prequote?.originalRent || 0;

  // Calculate the percentage of the original service fee
  const percentage = getOriginalServiceFee === 0 ? firstPaymentPercentage : 50;
  const achProcessingSavings = bookingStore?.achProcessingSavings || 0;
  // Calculate half of the total amount with taxes,fees and discount
  const countHalfTotalWithTaxesAndDiscount = (originalRent + hostFees + totalTax - discount - achProcessingSavings) * percentage / 100;
  // Calculate the full total with service fee and travel protection cost
  const countFullTotalWithFeesAndInsurance = serviceFee + insurance;
  // the final amount adjusted by the rate
  return (countHalfTotalWithTaxesAndDiscount + countFullTotalWithFeesAndInsurance) * rate;
}
interface ThreeStagePaymentBookingStore {
  isTravelProtected?: boolean;
  isCancelBookingTravelProtected?: boolean;
  prequote?: {
    originalServiceFee?: number | null;
  } | null;
}

// Function to calculate three stage payments (3 stage payment only)
export function calculateThreeStagePayment(rate: number, bookingStore: ThreeStagePaymentBookingStore, serviceFee: number, travelProtectionCost: number, formattedFinalTotal: string | number, thirdPaymentPercentage: number) {
  const isTravelProtected = bookingStore.isTravelProtected || false;
  const isCancelBookingTravelProtected = bookingStore.isCancelBookingTravelProtected || false;
  const getOriginalServiceFee = bookingStore?.prequote?.originalServiceFee || 0;

  // Calculate insurance amount -> If travel protection exists → add insurance , Else → insurance = 0
  const insurance = isTravelProtected && !isCancelBookingTravelProtected ? Number(travelProtectionCost) || 0 : isCancelBookingTravelProtected ? Number(travelProtectionCost) || 0 : 0;

  // Total Amount
  const numberValue = formattedFinalTotal?.toString().replace(/\$/g, "").replace(/,/g, "") || "0";
  const totalAmount = Number(numberValue);

  // Helper function to convert dollar values into cents to avoid floating-point precision issues
  const toCents = (value: number) => Math.round(value * 100);

  // Helper function to convert cents back into dollar format (Example: 1025 → 10.25).
  const fromCents = (cents: number) => Number((cents / 100).toFixed(2));
  const totalAmountCents = toCents(totalAmount); // Convert total amount into cents

  // First Payment (Calculate the full service fee and travel protection cost)
  let firstPaymentCents = toCents(serviceFee + insurance);
  const remainingCents = totalAmountCents - firstPaymentCents; // Remaining amount after first payment

  //  If original service fee exists, then use 50% split. - Otherwise use provided percentage.
  const percentage = getOriginalServiceFee === 0 ? thirdPaymentPercentage : 50;
  let secondPaymentCents: number;
  let thirdPaymentCents: number;

  // Special handling for 50/50 split to ensure the final two payments are always equal, even when the remaining amount contains odd cents.
  if (percentage === 50) {
    secondPaymentCents = Math.floor(remainingCents / 2); // Use floor so both payments stay equal and any odd cent is added to the first payment
    thirdPaymentCents = secondPaymentCents; // Make third payment same as second payment

    // Recalculate first payment so total amount stays perfectly balanced
    firstPaymentCents = totalAmountCents - secondPaymentCents - thirdPaymentCents;
  } else {
    // For non-50% split calculate normally
    secondPaymentCents = Math.round(remainingCents * percentage / 100);
    thirdPaymentCents = remainingCents - secondPaymentCents; // Third payment gets remaining balance
  }
  return {
    firstPayment: Number((fromCents(firstPaymentCents) * rate).toFixed(2)),
    secondPayment: Number((fromCents(secondPaymentCents) * rate).toFixed(2)),
    thirdPayment: Number((fromCents(thirdPaymentCents) * rate).toFixed(2))
  };
}